import type { NewUser, User } from '@/types/site';
import { useState } from 'react';

interface UseUserModalsReturn {
  deleteConfirmOpen: boolean;
  selectedUserForDelete: string | null;
  editModalOpen: boolean;
  selectedUserForEdit: User | null;
  addModalOpen: boolean;
  newUser: NewUser;
  passwordChangeOpen: boolean;
  actions: {
    openDeleteConfirm: (userId: string) => void;
    closeDeleteConfirm: () => void;
    openEditModal: (user: User) => void;
    closeEditModal: () => void;
    openAddModal: () => void;
    closeAddModal: () => void;
    openPasswordChange: () => void;
    closePasswordChange: () => void;
    updateSelectedUserForEdit: (user: User) => void;
    updateNewUser: (user: NewUser) => void;
    resetNewUser: () => void;
  };
}

const initialNewUser: NewUser = {
  loginId: '',
  password: '',
  passwordConfirm: '',
  name: '',
  email: '',
  fullAddress: '',
  mobilePhone: '',
  phone: '',
  userLevel: 'TOWN_MANAGER',
  smsReceive: true,
  description: '',
  siteIds: [],
};

export const useUserModals = (): UseUserModalsReturn => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedUserForDelete, setSelectedUserForDelete] = useState<
    string | null
  >(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<User | null>(
    null
  );
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newUser, setNewUser] = useState<NewUser>(initialNewUser);
  const [passwordChangeOpen, setPasswordChangeOpen] = useState(false);

  const openDeleteConfirm = (userId: string) => {
    setSelectedUserForDelete(userId);
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setSelectedUserForDelete(null);
  };

  const openEditModal = (user: User) => {
    setSelectedUserForEdit({ ...user });
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setSelectedUserForEdit(null);
  };

  const openAddModal = () => {
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
    setNewUser(initialNewUser);
  };

  const updateSelectedUserForEdit = (user: User) => {
    setSelectedUserForEdit(user);
  };

  const updateNewUser = (user: NewUser) => {
    setNewUser(user);
  };

  const resetNewUser = () => {
    setNewUser(initialNewUser);
  };

  const openPasswordChange = () => {
    setPasswordChangeOpen(true);
  };

  const closePasswordChange = () => {
    setPasswordChangeOpen(false);
  };

  return {
    deleteConfirmOpen,
    selectedUserForDelete,
    editModalOpen,
    selectedUserForEdit,
    addModalOpen,
    newUser,
    passwordChangeOpen,
    actions: {
      openDeleteConfirm,
      closeDeleteConfirm,
      openEditModal,
      closeEditModal,
      openAddModal,
      closeAddModal,
      openPasswordChange,
      closePasswordChange,
      updateSelectedUserForEdit,
      updateNewUser,
      resetNewUser,
    },
  };
};
